import { useState } from "react";
import {
  Alert, Box, CircularProgress, Dialog, DialogActions, DialogContent,
  Divider, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery } from "@tanstack/react-query";
import { genericNamesApi, type GenericNameDetailDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { useAuth } from "@/store/auth";

/**
 * Monograph pop-out for a pharmacy item that is linked to a generic master row.
 *
 * Renders nothing at all unless there is something to open: no linked generic, or no permission
 * to read the generic master, means no icon — an icon that opens an empty dialog is worse than
 * no icon. The detail is fetched only when the dialog opens, so putting this on a grid row costs
 * nothing until someone actually asks.
 *
 * Used anywhere an item is shown: the item master and its form, OP/IP billing lines, the
 * dispensing queue, and the substitute picker.
 */
export function GenericInfoButton({
  genericNameId, genericName, size = "small",
}: {
  genericNameId?: string | null;
  /** Shown in the tooltip and as the dialog heading before the fetch resolves. */
  genericName?: string | null;
  size?: "small" | "medium";
}) {
  const [open, setOpen] = useState(false);
  const canView = useAuth(s => s.hasPermission("catalog.generic.view"));

  if (!genericNameId || !canView) return null;

  return (
    <>
      <Tooltip title={genericName ? `Generic information — ${genericName}` : "Generic information"}>
        <IconButton
          size={size}
          color="primary"
          aria-label={genericName ? `Generic information for ${genericName}` : "Generic information"}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        >
          <InfoOutlinedIcon fontSize={size === "small" ? "small" : "medium"} />
        </IconButton>
      </Tooltip>
      {open && (
        <GenericInfoDialog
          id={genericNameId}
          fallbackName={genericName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** The monograph fields, in the order a pharmacist reads them. */
const FIELDS: { key: keyof GenericNameDetailDto; label: string }[] = [
  { key: "schedule", label: "Schedule" },
  { key: "therapeuticClassification", label: "Therapeutic classification" },
  { key: "composition", label: "Composition" },
  { key: "ingredients", label: "Ingredients" },
  { key: "uses", label: "Uses" },
  { key: "directionsForUse", label: "Directions for use" },
  { key: "contraindications", label: "Contraindications" },
  { key: "sideEffects", label: "Side effects" },
  { key: "precautionsAndWarnings", label: "Precautions and warnings" },
  { key: "safetyInformation", label: "Safety information" },
  { key: "countryOfOrigin", label: "Country of origin" },
];

function GenericInfoDialog({
  id, fallbackName, onClose,
}: { id: string; fallbackName?: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["generic-names", "detail", id],
    queryFn: () => genericNamesApi.get(id),
    staleTime: 5 * 60_000,
  });

  const g = q.data;
  // Only fields that actually carry a value — a blank heading tells the reader nothing.
  const present = g
    ? FIELDS.filter(f => {
        const v = g[f.key];
        return typeof v === "string" && v.trim().length > 0;
      })
    : [];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <ClosableDialogTitle onClose={onClose}>
        {g?.name ?? fallbackName ?? "Generic information"}
      </ClosableDialogTitle>
      <DialogContent dividers>
        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
        )}
        {q.isError && <Alert severity="error">{errorMessage(q.error)}</Alert>}

        {g && (
          <>
            <Box sx={{ mb: present.length ? 2 : 0 }}>
              <FieldHeading>Name</FieldHeading>
              <Typography variant="body2">{g.name}</Typography>
            </Box>

            {present.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No product information has been recorded for this generic yet. It can be added
                under Catalog → Generic Names.
              </Alert>
            ) : (
              present.map((f, i) => (
                <Box key={String(f.key)}>
                  {i > 0 && <Divider sx={{ my: 2 }} />}
                  <FieldHeading>{f.label}</FieldHeading>
                  {/* pre-wrap: pack-insert prose is captured with its own line breaks. */}
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {String(g[f.key])}
                  </Typography>
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function FieldHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block", mb: 0.5, color: "text.secondary",
        fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  );
}
